import { ArrowRight, FileText, Loader2, Search, Store, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import StatusBadge from "@/components/ui/StatusBadge";
import { formatINR } from "@/pages/admin/data";
import { adminService, mandateService } from "@/services/apiServices";

export default function AdminTopSearch() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mandates, setMandates] = useState<any[]>([]);
  const [retailers, setRetailers] = useState<any[]>([]);

  const containerRef = useRef<HTMLDivElement>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const performSearch = useCallback(async (searchTerm: string) => {
    const q = searchTerm.trim();
    if (q.length < 2) {
      setMandates([]);
      setRetailers([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const [mandatesRes, retailersRes] = await Promise.allSettled([
        mandateService.getMandates({ search: q, limit: 5 }),
        adminService.getRetailers({ search: q, limit: 3 }),
      ]);

      if (mandatesRes.status === "fulfilled" && Array.isArray(mandatesRes.value?.data?.mandates)) {
        setMandates(mandatesRes.value.data.mandates);
      } else {
        setMandates([]);
      }

      if (retailersRes.status === "fulfilled" && Array.isArray(retailersRes.value?.data?.retailers)) {
        setRetailers(retailersRes.value.data.retailers);
      } else {
        setRetailers([]);
      }
      setIsOpen(true);
    } catch (err) {
      console.error("Top bar database search error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setQuery(val);

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (val.trim().length >= 2) {
      debounceTimerRef.current = setTimeout(() => {
        performSearch(val);
      }, 250);
    } else {
      setMandates([]);
      setRetailers([]);
      setIsOpen(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      if (mandates.length === 1 && retailers.length === 0) {
        handleSelectMandate(mandates[0].refNo);
      } else if (retailers.length === 1 && mandates.length === 0) {
        handleSelectRetailer(retailers[0]);
      } else if (query.trim()) {
        setIsOpen(false);
        navigate(`/admin/mandates?search=${encodeURIComponent(query.trim())}`);
      }
    } else if (e.key === "Escape") {
      setIsOpen(false);
    }
  };

  const handleSelectMandate = (refNo: string) => {
    setIsOpen(false);
    setQuery("");
    navigate(`/admin/mandates/${refNo}`);
  };

  const handleSelectRetailer = (retailer: any) => {
    setIsOpen(false);
    setQuery("");
    navigate(`/admin/retailers?search=${encodeURIComponent(retailer.name || retailer.shopName || retailer.id)}`);
  };

  const handleClear = () => {
    setQuery("");
    setMandates([]);
    setRetailers([]);
    setIsOpen(false);
  };

  // Close on clicking outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, []);

  const hasResults = mandates.length > 0 || retailers.length > 0;

  return (
    <div ref={containerRef} className="relative hidden md:block">
      <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 flex h-5 w-5 items-center justify-center text-ink-muted">
        <Search className="h-[18px] w-[18px]" />
      </span>

      <input
        type="text"
        value={query}
        onChange={handleInputChange}
        onFocus={() => {
          if (query.trim().length >= 2) setIsOpen(true);
        }}
        onKeyDown={handleKeyDown}
        placeholder="Search by mobile, ref no or UMRN"
        className="h-10 w-[320px] rounded-lg border border-slate-300 bg-white pl-10 pr-9 text-sm text-ink placeholder:text-ink-muted focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30 transition-colors"
      />

      {loading ? (
        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 flex h-5 w-5 items-center justify-center text-primary-500">
          <Loader2 className="h-4 w-4 animate-spin" />
        </span>
      ) : query ? (
        <button
          type="button"
          onClick={handleClear}
          className="absolute right-2.5 top-1/2 -translate-y-1/2 flex h-6 w-6 items-center justify-center rounded-md text-slate-400 hover:text-slate-600 cursor-pointer"
          aria-label="Clear search"
        >
          <X className="h-4 w-4" />
        </button>
      ) : null}

      {/* Dynamic Results Dropdown */}
      {isOpen && query.trim().length >= 2 && (
        <div className="absolute left-0 top-full mt-2 w-[420px] max-w-[90vw] rounded-xl border border-line bg-white shadow-dropdown z-50 overflow-hidden animate-fadeIn">
          {loading && !hasResults ? (
            <div className="flex items-center justify-center gap-2 py-8 text-xs text-ink-secondary">
              <Loader2 className="h-4 w-4 animate-spin text-primary-500" />
              Searching database records...
            </div>
          ) : !hasResults ? (
            <div className="py-8 px-4 text-center">
              <p className="text-sm font-medium text-ink">No matching records found</p>
              <p className="mt-1 text-xs text-ink-secondary">
                No mandate or retailer found in database for &ldquo;{query}&rdquo;
              </p>
            </div>
          ) : (
            <div className="max-h-[380px] overflow-y-auto divide-y divide-slate-100">
              {/* Mandates Section */}
              {mandates.length > 0 && (
                <div className="p-2">
                  <div className="px-2 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-ink-muted">
                    Mandates ({mandates.length})
                  </div>
                  {mandates.map((m) => (
                    <button
                      key={m.id || m.refNo}
                      type="button"
                      onClick={() => handleSelectMandate(m.refNo)}
                      className="flex w-full items-center justify-between rounded-lg p-2.5 text-left hover:bg-slate-50 transition-colors cursor-pointer group"
                    >
                      <div className="flex items-start gap-2.5 min-w-0">
                        <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
                          <FileText className="h-4 w-4" />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-ink truncate group-hover:text-primary-600">
                              {m.customerName || "Customer"}
                            </span>
                            <span className="text-xs text-ink-secondary truncate">
                              · {m.customerMobile || m.refNo}
                            </span>
                          </div>
                          <div className="mt-0.5 flex items-center gap-2 text-xs text-ink-secondary">
                            <span className="font-mono text-[11px] text-primary-600">{m.refNo}</span>
                            {m.umrn && <span>· {m.umrn}</span>}
                            <span>· {formatINR(m.emiAmount || m.amount)}/mo</span>
                          </div>
                        </div>
                      </div>
                      <div className="shrink-0 pl-2">
                        <StatusBadge status={m.status} />
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {/* Retailers Section */}
              {retailers.length > 0 && (
                <div className="p-2">
                  <div className="px-2 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-ink-muted">
                    Retailers ({retailers.length})
                  </div>
                  {retailers.map((r) => (
                    <button
                      key={r.id}
                      type="button"
                      onClick={() => handleSelectRetailer(r)}
                      className="flex w-full items-center justify-between rounded-lg p-2.5 text-left hover:bg-slate-50 transition-colors cursor-pointer group"
                    >
                      <div className="flex items-start gap-2.5 min-w-0">
                        <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600">
                          <Store className="h-4 w-4" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-ink truncate group-hover:text-primary-600">
                            {r.shopName || r.name}
                          </p>
                          <p className="text-xs text-ink-secondary truncate">
                            {r.name} · {r.mobile || r.email || r.city || "—"}
                          </p>
                        </div>
                      </div>
                      <span
                        className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${
                          r.isActive !== false ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"
                        }`}
                      >
                        {r.isActive !== false ? "Active" : "Inactive"}
                      </span>
                    </button>
                  ))}
                </div>
              )}

              {/* Footer search all */}
              <div className="bg-slate-50 p-2 text-center">
                <button
                  type="button"
                  onClick={() => {
                    setIsOpen(false);
                    navigate(`/admin/mandates?search=${encodeURIComponent(query.trim())}`);
                  }}
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-primary-600 hover:text-primary-700 cursor-pointer"
                >
                  View all results in All Mandates
                  <ArrowRight className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
